import { useProviderConfig } from '@servicetitan/financing-workflow-shared';

export interface LoanAmountValidationResult {
    /** Whether the amount is within the valid range */
    isValidLoanAmount: boolean;
    /** Whether the amount is below the minimum */
    isBelowMinimum: boolean;
    /** Whether the amount is above the maximum */
    isAboveMaximum: boolean;
    /** The minimum amount from provider config, if any */
    minAmount?: number;
    /** The maximum amount from provider config, if any */
    maxAmount?: number;
}

/**
 * Hook that validates loan amounts against provider-specific minimum and maximum limits
 *
 * @param amount The loan amount to validate
 * @returns Validation result with detailed information about the validation state
 *
 * @example
 * ```tsx
 * const { isValidLoanAmount, minAmount, isBelowMinimum } = useLoanAmountValidation(5000);
 *
 * if (!isValidLoanAmount) {
 *   return <InvalidLoanAmountState minimumLoanAmount={minAmount} />;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // For API call control
 * const { isValidLoanAmount } = useLoanAmountValidation(financedAmount);
 *
 * const { data, isLoading } = useQuery({
 *   queryKey: ['plans', params],
 *   queryFn: fetchPlans,
 *   enabled: isValidLoanAmount, // Prevents API call when amount is invalid
 * });
 * ```
 */
export const useLoanAmountValidation = (amount?: number): LoanAmountValidationResult => {
    let loanAmountValidation;
    try {
        const config = useProviderConfig();
        loanAmountValidation = config.loanAmountValidation;
    } catch {
        loanAmountValidation = undefined;
    }

    // If no amount provided, consider it invalid to prevent API calls
    if (amount == null) {
        return {
            isValidLoanAmount: false,
            isBelowMinimum: false,
            isAboveMaximum: false,
            minAmount: loanAmountValidation?.minAmount,
            maxAmount: loanAmountValidation?.maxAmount,
        };
    }

    // If no validation config, consider any valid amount as valid
    if (!loanAmountValidation) {
        return {
            isValidLoanAmount: true,
            isBelowMinimum: false,
            isAboveMaximum: false,
            minAmount: undefined,
            maxAmount: undefined,
        };
    }

    const { minAmount, maxAmount } = loanAmountValidation;

    const isBelowMinimum = minAmount != null && amount < minAmount;
    const isAboveMaximum = maxAmount != null && amount > maxAmount;
    const isValidLoanAmount = !isBelowMinimum && !isAboveMaximum;

    return {
        isValidLoanAmount,
        isBelowMinimum,
        isAboveMaximum,
        minAmount,
        maxAmount,
    };
};
